<script setup>
import { computed, ref } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  showUnpin: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['unpin', 'jumpToMessage']);

const isCollapsed = ref(true);
const MAX_PREVIEW_LENGTH = 80;

const latestMessage = computed(() => {
  if (!props.messages.length) return null;
  return props.messages[props.messages.length - 1];
});

const remainingCount = computed(() => {
  return Math.max(0, props.messages.length - 1);
});

const hasMoreMessages = computed(() => {
  return props.messages.length > 1;
});

const getMessagePreview = message => {
  if (!message.content) return '';
  return message.content.length > MAX_PREVIEW_LENGTH
    ? message.content.substring(0, MAX_PREVIEW_LENGTH) + '...'
    : message.content;
};

// Get sender name
const getSenderName = message => {
  if (!message.sender) return '';
  return message.sender.name || message.sender.email || 'Unknown';
};

// Jump to specific message
const jumpToMessage = message => {
  emit('jumpToMessage', message);
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: message.id,
  });
};

// Toggle collapse state
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};

// Handle unpin
const handleUnpin = message => {
  emit('unpin', message);
};
</script>

<template>
  <div v-if="messages.length" class="pinned-smart">
    <!-- Smart Header -->
    <div class="smart-header">
      <div class="header-left">
        <lucide-icon name="pin" size="16" class="pin" />
        <span class="title">{{ $t('CONVERSATION.PINNED_MESSAGES.TITLE', { count: messages.length }) }}</span>
      </div>
      <button
        v-if="hasMoreMessages"
        class="toggle-btn"
        @click="toggleCollapse"
      >
        <lucide-icon :name="isCollapsed ? 'chevron-down' : 'chevron-up'" size="16" />
      </button>
    </div>

    <!-- Smart Collapsed View -->
    <div v-if="isCollapsed && latestMessage" class="smart-preview">
      <div class="preview-content">
        <div class="preview-meta">
          <span class="sender">{{ getSenderName(latestMessage) }}</span>
          <div class="actions">
            <button class="action-link" @click="jumpToMessage(latestMessage)">
              {{ $t('CONVERSATION.PINNED_MESSAGES.JUMP_TO_MESSAGE') }}
            </button>
            <button v-if="showUnpin" class="action-link unpin" @click="handleUnpin(latestMessage)">
              {{ $t('CONVERSATION.PINNED_MESSAGES.UNPIN') }}
            </button>
          </div>
        </div>
        <p class="message-text">{{ getMessagePreview(latestMessage) }}</p>
      </div>
      
      <!-- Smart More Indicator -->
      <div v-if="hasMoreMessages" class="more-smart">
        <span>{{ $t('CONVERSATION.PINNED_MESSAGES.MORE_MESSAGES', { count: remainingCount }) }}</span>
      </div>
    </div>

    <!-- Smart Expanded View -->
    <div v-else class="smart-list">
      <div
        v-for="(message, index) in messages"
        :key="message.id"
        class="smart-item"
      >
        <div class="item-content">
          <div class="item-meta">
            <span class="item-sender">{{ getSenderName(message) }}</span>
            <div class="item-actions">
              <button class="action-link" @click="jumpToMessage(message)">
                {{ $t('CONVERSATION.PINNED_MESSAGES.JUMP_TO_MESSAGE') }}
              </button>
              <button v-if="showUnpin" class="action-link unpin" @click="handleUnpin(message)">
                {{ $t('CONVERSATION.PINNED_MESSAGES.UNPIN') }}
              </button>
            </div>
          </div>
          <p class="item-text">{{ message.content }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pinned-smart {
  @apply mb-2 transition-all duration-200;
}

/* Header: mỏng, gọn */
.smart-header {
  @apply flex items-center justify-between mb-1 px-2 py-1 text-xs text-slate-600 dark:text-slate-300;
}

.header-left {
  @apply flex items-center gap-1;
}

.pin-icon {
  @apply text-hichat-500;
}

.count {
  @apply text-[11px] font-semibold text-hichat-600 bg-hichat-100 dark:bg-hichat-900/40 px-1.5 py-0.5 rounded;
}

.title {
  @apply text-xs font-medium text-slate-700 dark:text-slate-200;
}

.toggle-btn {
  @apply p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors;
}

/* Preview: 1 thẻ nhỏ, nền nhẹ */
.smart-preview {
  @apply rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 transition-all duration-200;
}

.preview-content {
  @apply px-3 py-2;
}

.message-text {
  @apply text-xs text-slate-800 dark:text-slate-200 mb-1 leading-snug line-clamp-2;
}

.preview-meta {
  @apply flex items-center justify-between gap-2;
}

.sender {
  @apply text-[11px] text-slate-500 dark:text-slate-400;
}

.actions {
  @apply flex items-center gap-2;
}

.action-link {
  @apply text-[11px] font-medium text-hichat-500 hover:text-hichat-600 dark:text-hichat-400 dark:hover:text-hichat-300 transition-colors;
}

.action-link.unpin {
  @apply text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300;
}

.more-smart {
  @apply px-3 py-1 text-[11px] text-slate-500 dark:text-slate-400 border-t border-slate-200/80 dark:border-slate-700/80 dark:bg-slate-900/40;
}

/* List mở rộng: thẻ nhỏ, phẳng */
.smart-list {
  @apply space-y-1.5;
}

.smart-item {
  @apply rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 transition-all duration-200;
}

.item-content {
  @apply px-3 py-2;
}

.item-text {
  @apply text-xs text-slate-800 dark:text-slate-200 mb-1 leading-snug;
}

.item-meta {
  @apply flex items-center justify-between gap-2;
}

.item-sender {
  @apply text-[11px] text-slate-500 dark:text-slate-400;
}

.item-actions {
  @apply flex items-center gap-2;
}

/* Responsive */
@media (max-width: 640px) {
  .preview-meta,
  .item-meta {
    @apply flex-col items-start gap-1.5;
  }

  .actions,
  .item-actions {
    @apply flex-wrap gap-1;
  }

  .message-text,
  .item-text {
    @apply line-clamp-2;
  }
}

/* Simple animations */
.smart-preview,
.smart-item {
  @apply transition-all duration-200;
}

.smart-preview:hover,
.smart-item:hover {
  @apply shadow-sm;
}
</style>
